import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import DataTable from "@/components/admin/DataTable";
import DetailLayout from "@/components/admin/DetailLayout";
import StatusBadge from "@/components/admin/StatusBadge";
import Timeline from "@/components/admin/Timeline";
import EmptyState from "@/components/admin/EmptyState";
import ConfirmActionDialog from "@/components/admin/ConfirmActionDialog";
import { Button } from "@/components/ui/button";
import {
  Image as ImageIcon, Palette, Download as DLIcon,
  Archive, ArchiveRestore, Trash2, Undo2, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-AU");
}

function tone(status) {
  if (status === "active") return "grey";
  if (status === "unlocked") return "green";
  if (status === "archived") return "amber";
  if (status === "deleted") return "red";
  return "grey";
}

const ACTIONS = {
  archive: {
    title: "Archive this project",
    description: "Archived projects disappear from the customer's workspace and the default project list, but data is preserved.",
    confirmLabel: "Archive",
    endpoint: (id) => `/admin/projects/${id}/archive`,
    success: "Project archived",
    body: (reason) => ({ reason }),
  },
  unarchive: {
    title: "Unarchive this project",
    description: "The project returns to the customer's workspace.",
    confirmLabel: "Unarchive",
    endpoint: (id) => `/admin/projects/${id}/unarchive`,
    success: "Project unarchived",
    body: () => ({}),
  },
  softDelete: {
    title: "Soft-delete this project (Owner only)",
    description: "Customer-facing removal. Data is preserved and Owner/Admin can restore from the same screen. No data is permanently removed.",
    confirmLabel: "Soft-delete",
    destructive: true,
    endpoint: (id) => `/admin/projects/${id}/soft-delete`,
    success: "Project removed",
    body: (reason) => ({ reason }),
  },
  restore: {
    title: "Restore this project",
    description: "Restores the project from either archived or deleted state and returns it to the customer's workspace.",
    confirmLabel: "Restore",
    endpoint: (id) => `/admin/projects/${id}/restore`,
    success: "Project restored",
    body: () => ({}),
  },
};

function TabPaginated({ endpoint, columns, testId, emptyTitle, emptyCaption, emptyIcon, refreshKey }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    api.get(`${endpoint}?${params.toString()}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [endpoint, page, pageSize, refreshKey]);

  return (
    <DataTable
      testId={testId}
      columns={columns}
      rows={data.items}
      total={data.total}
      page={page}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
      loading={loading}
      empty={<EmptyState icon={emptyIcon} title={emptyTitle} caption={emptyCaption} testId={`${testId}-empty`} />}
    />
  );
}

function RendersGrid({ projectId, refreshKey }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/projects/${projectId}/renders?page=1&page_size=24`)
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  }, [projectId, refreshKey]);

  if (loading) return <div className="text-sm text-brand-muted">Loading renders…</div>;
  if (items.length === 0) {
    return <EmptyState icon={ImageIcon} title="No renders yet" caption="This project hasn't generated any renders." testId="project-renders-empty" />;
  }
  return (
    <div data-testid="project-renders-grid">
      <div className="overline mb-3">Renders ({total})</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((r) => (
          <div key={r.id} className="bg-white border border-brand-linen p-3" data-testid={`render-card-${r.id}`}>
            <div className="aspect-square bg-brand-linen/50 flex items-center justify-center mb-2">
              <div className="w-10 h-10" style={{ background: r.color_hex || "#ccc" }} />
            </div>
            <div className="text-xs font-mono text-brand-muted truncate">{r.surface}</div>
            <div className="text-sm text-brand-ink truncate">{r.color_name || "—"}</div>
            <div className="text-xs text-brand-muted mt-1">
              <StatusBadge tone={r.status === "ready" ? "green" : r.status === "failed" ? "red" : "amber"} label={r.status} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminProjectDetail({ projectId, onBack }) {
  const { user: actor } = useAuth();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("overview");
  const [err, setErr] = useState("");
  const [dlg, setDlg] = useState(null); // {kind} | null
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [photoUrl, setPhotoUrl] = useState(null);

  const reload = useCallback(() => {
    api.get(`/admin/projects/${projectId}`)
      .then((r) => { setP(r.data); setErr(""); })
      .catch((e) => setErr(e.message || "Failed to load"));
  }, [projectId]);

  useEffect(() => { reload(); }, [reload]);

  // Photo proxy fetched with auth, then converted to a blob URL for the <img> tag.
  useEffect(() => {
    let cancel = false; let blobUrl = null;
    api.get(`/admin/projects/${projectId}/photo`, { responseType: "blob" })
      .then((r) => {
        if (cancel) return;
        blobUrl = URL.createObjectURL(r.data);
        setPhotoUrl(blobUrl);
      })
      .catch(() => { /* no photo or fetch failed */ });
    return () => {
      cancel = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [projectId, refreshKey]);

  const onConfirm = async (reason) => {
    if (!dlg) return;
    const cfg = ACTIONS[dlg.kind];
    if (!cfg) return;
    setBusy(true);
    try {
      await api.post(cfg.endpoint(projectId), cfg.body(reason));
      toast.success(cfg.success);
      setDlg(null);
      reload();
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  if (err) {
    return <div className="text-sm border border-destructive/30 bg-destructive/5 text-destructive px-4 py-3">{err}</div>;
  }
  if (!p) return <div className="text-sm text-brand-muted">Loading project…</div>;

  const isOwnerActor = !!actor?.is_owner;
  const isDeleted = p.status === "deleted";
  const isArchived = p.status === "archived";
  const L = p.lifetime || {};

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "renders",  label: "Renders",  count: L.renders },
    { key: "schemes",  label: "Schemes",  count: L.schemes },
    { key: "downloads",label: "Downloads" },
    { key: "timeline", label: "Timeline" },
  ];

  const actions = (
    <>
      {isArchived ? (
        <Button
          data-testid="action-unarchive"
          variant="outline" size="sm"
          onClick={() => setDlg({ kind: "unarchive" })}
          disabled={isDeleted}
        >
          <ArchiveRestore className="w-4 h-4 mr-1" /> Unarchive
        </Button>
      ) : (
        <Button
          data-testid="action-archive"
          variant="outline" size="sm"
          onClick={() => setDlg({ kind: "archive" })}
          disabled={isDeleted}
        >
          <Archive className="w-4 h-4 mr-1" /> Archive
        </Button>
      )}
      {(isDeleted || isArchived) && (
        <Button
          data-testid="action-restore"
          variant="outline" size="sm"
          onClick={() => setDlg({ kind: "restore" })}
        >
          <Undo2 className="w-4 h-4 mr-1" /> Restore
        </Button>
      )}
      {isOwnerActor && !isDeleted && (
        <Button
          data-testid="action-soft-delete"
          variant="outline" size="sm"
          onClick={() => setDlg({ kind: "softDelete" })}
        >
          <Trash2 className="w-4 h-4 mr-1" /> Soft-delete
        </Button>
      )}
    </>
  );

  let banner = null;
  if (isDeleted) {
    const byCustomer = p.deleted_by_kind === "customer";
    banner = (
      <div className="text-xs px-3 py-2 bg-red-50 border border-red-200 text-red-700" data-testid="project-banner-deleted">
        Deleted by {p.deleted_by_kind || "—"}{p.deleted_at ? ` · ${relTime(p.deleted_at)}` : ""}
        {p.deleted_reason ? ` · reason: ${p.deleted_reason}` : ""}
        {byCustomer && " · customer can self-restore for 30 days from deletion"}
      </div>
    );
  } else if (isArchived) {
    banner = (
      <div className="text-xs px-3 py-2 bg-amber-50 border border-amber-200 text-amber-700" data-testid="project-banner-archived">
        Archived{p.archived_at ? ` · ${relTime(p.archived_at)}` : ""}
        {p.archived_reason ? ` · reason: ${p.archived_reason}` : ""}
      </div>
    );
  } else if (p.unlocked_by_admin) {
    banner = (
      <div className="text-xs px-3 py-2 bg-blue-50 border border-blue-200 text-blue-700" data-testid="project-banner-admin-unlocked">
        Manually unlocked by {p.unlocked_by_admin_email || "—"}
        {p.unlock_admin_reason ? ` · "${p.unlock_admin_reason}"` : ""}
        {p.unlock_transaction_id ? ` · ${p.unlock_transaction_id}` : ""}
      </div>
    );
  }

  const dlgCfg = dlg ? ACTIONS[dlg.kind] : null;

  return (
    <>
      <DetailLayout
        testId="admin-project-detail"
        onBack={onBack}
        backLabel="← Back to projects"
        title={p.title}
        status={<StatusBadge tone={tone(p.status)} label={p.status} testId="project-status" />}
        subText={(
          <>
            Owner ·&nbsp;
            <span className="font-mono text-xs">{p.owner_email || "—"}</span>
            &nbsp;· {L.renders ?? 0} renders · {L.schemes ?? 0} schemes · updated {relTime(p.updated_at)}
          </>
        )}
        actions={actions}
        banner={banner}
        tabs={tabs}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === "overview" && (
          <div data-testid="project-tab-overview" className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-brand-linen p-4">
              <div className="overline mb-3">Original photo</div>
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt="Original"
                  className="w-full border border-brand-linen"
                  data-testid="project-photo"
                />
              ) : (
                <div className="aspect-video bg-brand-linen/30 flex items-center justify-center text-xs text-brand-muted">
                  Loading photo…
                </div>
              )}
              <div className="text-xs text-brand-muted mt-2">
                {p.photo_count} photo{p.photo_count === 1 ? "" : "s"} attached
              </div>
            </div>
            <div className="bg-white border border-brand-linen p-4">
              <div className="overline mb-3">Project metadata</div>
              <Row label="Status" value={<StatusBadge tone={tone(p.status)} label={p.status} />} />
              <Row label="Surface default" value={p.surface_default} />
              <Row label="Scope" value={p.project_scope} />
              <Row label="Unlocked" value={p.unlocked ? "Yes" : "No"} />
              {p.unlocked && p.unlock_transaction_id && (
                <Row label="Unlock ref" mono value={p.unlock_transaction_id} />
              )}
              <Row label="Created" value={p.created_at ? new Date(p.created_at).toLocaleString("en-AU") : "—"} />
              <Row label="Updated" value={relTime(p.updated_at)} />
              <div className="mt-4 overline">Owner</div>
              <Row label="Email" mono value={p.owner_email} />
              <Row label="Name" value={p.owner_name} />
              <a
                href="#"
                data-testid="open-customer-link"
                onClick={(e) => {
                  e.preventDefault();
                  // Switch back to admin Customers tab + select this customer.
                  // Simple approach: fire a custom event so the parent can react.
                  window.location.hash = `customer:${p.owner_user_id}`;
                  toast.info("Open the Customers tab to view full customer detail");
                }}
                className="inline-flex items-center gap-1 text-xs text-brand-teal mt-2 hover:underline"
              >
                <ExternalLink className="w-3.5 h-3.5" /> View customer
              </a>
              <div className="mt-4 overline">Lifetime</div>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <Stat label="Renders" value={L.renders} />
                <Stat label="Ready" value={L.ready_renders} />
                <Stat label="Failed" value={L.failed_renders} />
                <Stat label="Schemes" value={L.schemes} />
                <Stat label="Payments" value={L.payments} />
              </div>
            </div>
          </div>
        )}

        {tab === "renders" && (
          <div data-testid="project-tab-renders">
            <RendersGrid projectId={projectId} refreshKey={refreshKey} />
          </div>
        )}

        {tab === "schemes" && (
          <div data-testid="project-tab-schemes">
            <TabPaginated
              endpoint={`/admin/projects/${projectId}/schemes`}
              testId="project-schemes-table"
              refreshKey={refreshKey}
              emptyIcon={Palette} emptyTitle="No schemes yet" emptyCaption="No saved colour schemes for this project."
              columns={[
                { key: "name", label: "Name", render: (r) => r.name || "(untitled)" },
                { key: "created_at", label: "Created", render: (r) => relTime(r.created_at) },
              ]}
            />
          </div>
        )}

        {tab === "downloads" && (
          <div data-testid="project-tab-downloads">
            <TabPaginated
              endpoint={`/admin/projects/${projectId}/downloads`}
              testId="project-downloads-table"
              refreshKey={refreshKey}
              emptyIcon={DLIcon} emptyTitle="No downloads yet" emptyCaption="No download events recorded for this project."
              columns={[
                { key: "kind", label: "Type", render: (r) => <span className="font-mono text-xs">{r.kind}</span> },
                { key: "summary", label: "Detail", render: (r) => r.summary },
                { key: "created_at", label: "When", render: (r) => relTime(r.created_at) },
              ]}
            />
          </div>
        )}

        {tab === "timeline" && (
          <div data-testid="project-tab-timeline">
            <Timeline
              endpoint={`/admin/projects/${projectId}/timeline`}
              testId="project-timeline"
              refreshKey={refreshKey}
            />
          </div>
        )}
      </DetailLayout>

      {dlgCfg && (
        <ConfirmActionDialog
          open
          onClose={() => setDlg(null)}
          onConfirm={onConfirm}
          title={dlgCfg.title}
          description={dlgCfg.description}
          confirmLabel={dlgCfg.confirmLabel}
          destructive={dlgCfg.destructive}
          busy={busy}
          testId={`dialog-${dlg.kind}`}
        />
      )}
    </>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-brand-linen/40 last:border-0">
      <div className="text-xs uppercase tracking-wider text-brand-muted">{label}</div>
      <div className={`text-sm text-brand-ink text-right ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</div>
    </div>
  );
}
function Stat({ label, value }) {
  return (
    <div className="text-center">
      <div className="font-serif text-xl text-brand-ink">{value ?? 0}</div>
      <div className="text-xs text-brand-muted">{label}</div>
    </div>
  );
}
